import React, { useState, useRef, useCallback } from "react";
import { Mic, Square, Loader2 } from "lucide-react";
import { authFetch, apiUrl } from "../utils/api";

const VoiceInputButton = ({ onTranscriptionComplete, disabled = false }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [error, setError] = useState(null);

  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);
  const streamRef = useRef(null);
  const mimeTypeRef = useRef("");

  // Maximum recording time (10 minutes)
  const MAX_RECORDING_TIME = 10 * 60; // 600 seconds

  const startTimer = useCallback(() => {
    setRecordingTime(0);
    timerRef.current = setInterval(() => {
      setRecordingTime((prevTime) => {
        const newTime = prevTime + 1;
        if (newTime >= MAX_RECORDING_TIME) {
          stopRecording();
          return prevTime;
        }
        return newTime;
      });
    }, 1000);
  }, []);

  const stopTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setRecordingTime(0);
  }, []);

  const formatTime = useCallback((seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  }, []);

  const startRecording = async () => {
    try {
      setError(null);
      setIsProcessing(true);

      // Request microphone permission
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          sampleRate: 16000,
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
        },
      });

      streamRef.current = stream;

      // Start recording
      audioChunksRef.current = [];

      const supportedMimeType = [
        "audio/webm;codecs=opus",
        "audio/webm",
        "audio/mp4",
      ].find((type) => MediaRecorder.isTypeSupported?.(type));
      mimeTypeRef.current = supportedMimeType || "";
      const mediaRecorder = supportedMimeType
        ? new MediaRecorder(stream, { mimeType: supportedMimeType })
        : new MediaRecorder(stream);

      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        setIsProcessing(true);

        try {
          // Combine all audio chunks
          const audioBlob = new Blob(audioChunksRef.current, {
            type: mimeTypeRef.current || mediaRecorder.mimeType || "audio/webm",
          });

          // Send to server for transcription
          const transcription = await transcribeAudio(audioBlob);

          if (transcription && onTranscriptionComplete) {
            onTranscriptionComplete(transcription);
          }
        } catch (error) {
          console.error("Transcription error:", error);
          setError("Transcription failed. Please try again.");
        } finally {
          cleanup();
        }
      };

      mediaRecorder.start(250); // Collect data every 250ms
      setIsRecording(true);
      setIsProcessing(false);
      startTimer();
    } catch (error) {
      console.error("Recording error:", error);
      setError(
        error.name === "NotAllowedError"
          ? "Microphone permission denied"
          : "Failed to start recording",
      );
      setIsProcessing(false);
      cleanup();
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      stopTimer();
    }
  };

  const transcribeAudio = async (audioBlob) => {
    console.log("🎤 Starting transcription...", {
      blobSize: audioBlob.size,
      blobType: audioBlob.type,
    });

    // Create FormData for file upload
    const formData = new FormData();
    const extension = audioBlob.type.includes("mp4") ? "m4a" : "webm";
    formData.append("audio", audioBlob, `recording.${extension}`);

    // Send to server endpoint with proper Clerk authentication
    const response = await authFetch(apiUrl("/api/audio/transcribe"), {
      method: "POST",
      body: formData,
    });

    console.log("📥 Response status:", response.status, response.ok);

    // Always read body as text first (can only read once)
    const responseText = await response.text();
    console.log("📥 Response body:", responseText.substring(0, 200));

    // Parse JSON from text
    let data;
    try {
      data = JSON.parse(responseText);
    } catch (parseErr) {
      console.error("❌ Failed to parse JSON:", parseErr.message);
      throw new Error("Invalid response from server");
    }

    // Handle error responses
    if (!response.ok) {
      console.error("❌ Server error:", data);
      throw new Error(data.details || data.error || "Transcription failed");
    }

    // Extract transcript
    const transcript = data.transcript;
    if (!transcript) {
      console.warn("⚠️ No transcript in response:", data);
      throw new Error(data.details || "No speech detected");
    }

    console.log("✅ Transcription successful:", transcript.substring(0, 50));
    return transcript;
  };

  const cleanup = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }

    stopTimer();
    setIsRecording(false);
    setIsProcessing(false);
    setRecordingTime(0);
    audioChunksRef.current = [];
  }, [stopTimer]);

  // Cleanup on unmount
  React.useEffect(() => {
    return cleanup;
  }, [cleanup]);

  const getButtonContent = () => {
    if (isProcessing) {
      return <Loader2 size={18} className="spin" />;
    }
    if (isRecording) {
      return <Square size={16} fill="currentColor" />;
    }
    return <Mic size={18} />;
  };

  const getButtonTitle = () => {
    if (isProcessing) {
      return "Processing...";
    }
    if (isRecording) {
      return `Recording... ${formatTime(recordingTime)} / ${formatTime(MAX_RECORDING_TIME)}`;
    }
    return "Start voice input (up to 10 minutes)";
  };

  return (
    <div className="voice-input-container">
      <button
        type="button"
        onClick={isRecording ? stopRecording : startRecording}
        disabled={disabled || isProcessing}
        className={`chat-action-btn ${isRecording ? "is-recording" : ""} ${isProcessing ? "is-active" : ""}`}
        aria-label={getButtonTitle()}
        title={getButtonTitle()}
      >
        {getButtonContent()}
      </button>

      {isRecording && (
        <div
          className="recording-timer"
          style={{
            position: "absolute",
            top: "-25px",
            left: "50%",
            transform: "translateX(-50%)",
            fontSize: "12px",
            color: "var(--accent-danger)",
            fontWeight: "bold",
            whiteSpace: "nowrap",
          }}
        >
          {formatTime(recordingTime)}
        </div>
      )}

      {error && (
        <div
          className="voice-error"
          role="alert"
          style={{
            position: "absolute",
            bottom: "50px",
            left: "50%",
            transform: "translateX(-50%)",
            fontSize: "12px",
            color: "var(--accent-danger)",
            maxWidth: "200px",
            textAlign: "center",
            wordWrap: "break-word",
            padding: "6px 12px",
            backgroundColor: "rgba(220, 53, 69, 0.1)",
            borderRadius: "8px",
            border: "1px solid rgba(220, 53, 69, 0.3)",
            boxShadow: "0 2px 8px rgba(220, 53, 69, 0.2)",
            zIndex: 1000,
          }}
        >
          {error}
        </div>
      )}
    </div>
  );
};

export default VoiceInputButton;
